import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContributionPersonRef } from '@gwc/shared';
import { tastingsApi } from '../api/tastings';
import { PersonPicker } from './PersonPicker';
import { ApiError } from '../lib/api';

/**
 * Who a test event is allowed to message.
 *
 * This panel is the whole safety story of a rehearsal, so it states the rule in
 * plain words rather than assuming anyone remembers it: pick any audience you
 * like in the composer, and the message still reaches only these people. An
 * empty roster reaches nobody, which is why the empty state says so outright
 * instead of looking like a panel that hasn't loaded.
 */
export function TestRoster({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [person, setPerson] = useState<ContributionPersonRef | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: roster = [], isLoading } = useQuery({
    queryKey: ['test-participants', eventId],
    queryFn: () => tastingsApi.testParticipants(eventId),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['test-participants', eventId] });
    // The composer's reach counts are clamped to this roster, so they're stale
    // the moment it changes.
    void qc.invalidateQueries({ queryKey: ['event-comms', eventId] });
  };

  const add = useMutation({
    mutationFn: (personId: string) => tastingsApi.addTestParticipant(eventId, personId),
    onSuccess: () => {
      setPerson(null);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not add that tester'),
  });

  const remove = useMutation({
    mutationFn: (personId: string) => tastingsApi.removeTestParticipant(eventId, personId),
    onSuccess: invalidate,
  });

  return (
    <div className="card mb-6 border-warning/30">
      <h2 className="mb-1 text-sm uppercase tracking-wide text-warning">Testers</h2>
      <p className="mb-4 text-xs text-cream-300/60">
        Every message from this tasting — announcements, RSVP confirmations, reminders, the live
        pushes — reaches these people and nobody else, whatever audience you pick when you send it.
        Add real members so you can test what actually lands in an inbox.
      </p>

      {isLoading ? (
        <p className="text-sm text-cream-300/50">Loading…</p>
      ) : roster.length === 0 ? (
        <p className="mb-4 text-sm text-warning">
          No testers yet — messages from this tasting currently reach nobody.
        </p>
      ) : (
        <ul className="mb-4 space-y-2">
          {roster.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-3 rounded border border-ink-800 px-3 py-2"
            >
              <span className="text-sm text-cream-100">
                {r.firstName} {r.lastName}
                {r.email && <span className="block text-xs text-cream-300/50">{r.email}</span>}
              </span>
              <button
                type="button"
                className="btn-ghost px-2 py-1 text-xs"
                disabled={remove.isPending}
                onClick={() => remove.mutate(r.personId)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="mb-2 text-sm text-wine-300">{error}</p>}
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-[12rem] flex-1">
          <PersonPicker value={person} onChange={setPerson} placeholder="Add a member…" />
        </div>
        <button
          type="button"
          className="btn-primary px-3 py-2 text-xs"
          disabled={!person?.personId || add.isPending}
          onClick={() => person?.personId && add.mutate(person.personId)}
        >
          {add.isPending ? 'Adding…' : 'Add tester'}
        </button>
      </div>
    </div>
  );
}
